import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TierCard from "../components/TierCard";
import { Spinner } from "@/shared/components/ui/spinner";
import {
  type IMembershipPlan,
  type MembershipBillingType,
} from "../interfaces/membership.interface";
import {
  getMembershipStatus,
  setupMembership,
  useMembershipLevels,
  useMembershipStatus,
} from "../services";
import { useProfile } from "@/features/profile/services";
import {
  mapMembershipLevelToPlan,
  sortMembershipLevels,
} from "../utils/membership";
import {
  clearMembershipSelection,
  storeMembershipSelection,
} from "@/shared/utils/membership";
import { toast } from "sonner";
import { isApiError } from "@/shared/api";
import {
  resolveEduKycUrlAfterMembershipSetup,
  resolveRouteAfterPlanSelection,
  resolveRouteAfterSetupMembership,
  resolveRouteForActiveMembership,
} from "../lib/membership-flow";
import { isKycVerifiedStatus } from "@/features/kyc/utils/kyc-state";

type MembershipTab = {
  label: string;
  billingType: MembershipBillingType;
  plans: IMembershipPlan[];
};

export default function Membership() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useMembershipLevels();
  const { refetch: refetchMembershipStatus } = useMembershipStatus({
    retry: false,
    staleTime: 30_000,
    enabled: false,
  });
  const { refetch: refetchProfile } = useProfile({
    retry: 1,
    staleTime: 30_000,
    enabled: false,
  });
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);

  const sortedLevels = useMemo(() => {
    if (!data || data.length === 0) return [];
    return sortMembershipLevels(data);
  }, [data]);

  const monthlyPlans = useMemo(() => {
    return sortedLevels.map((level, index) =>
      mapMembershipLevelToPlan(level, "MONTHLY", index)
    );
  }, [sortedLevels]);

  const yearlyPlans = useMemo(() => {
    return sortedLevels.map((level, index) =>
      mapMembershipLevelToPlan(level, "ANNUAL", index)
    );
  }, [sortedLevels]);

  const tabs = useMemo<MembershipTab[]>(
    () => [
      {
        label: "Monthly",
        billingType: "MONTHLY",
        plans: monthlyPlans,
      },
      {
        label: "Yearly",
        billingType: "ANNUAL",
        plans: yearlyPlans,
      },
    ],
    [monthlyPlans, yearlyPlans]
  );

  const [activeLabel, setActiveLabel] = useState<MembershipTab["label"]>(
    tabs[0]?.label ?? "Monthly"
  );

  const activeTab = tabs.find((tab) => tab.label === activeLabel) ??
    tabs[0] ?? {
      label: "Monthly",
      plans: [],
    };
  const activePlans = activeTab.plans;

  const handlePlanSelection = async (
    plan: IMembershipPlan,
    displayedLabel: string,
    billingType: MembershipBillingType
  ) => {
    const identifier =
      plan.id ?? `${plan.planCode ?? ""}-${plan.name}-${billingType}`;
    setPendingPlanId(identifier);

    try {
      const profileResult = await refetchProfile();
      const isKycVerified = isKycVerifiedStatus(
        profileResult.data?.kyc?.status ?? null
      );
      const membershipStatus = await getMembershipStatus().catch((error) => {
        if (
          isApiError(error) &&
          (error.errorName === "membership-not-found" ||
            error.errorCode === "404103")
        ) {
          return null;
        }
        throw error;
      });

      if (membershipStatus?.status?.toUpperCase() === "ACTIVE") {
        const eduKycUrl = resolveEduKycUrlAfterMembershipSetup({
          kycUrl: profileResult.data?.kycUrl,
          eduKycUrl: profileResult.data?.eduKycUrl,
          isKycVerified,
        });

        if (eduKycUrl) {
          clearMembershipSelection();
          window.location.assign(eduKycUrl);
          return;
        }

        toast.info("Your membership is already active.");
        clearMembershipSelection();
        navigate(resolveRouteForActiveMembership({ isKycVerified }), {
          replace: true,
        });
        return;
      }

      const storedPlan = {
        id: plan.id,
        name: plan.name,
        billingType: plan.billingType ?? billingType ?? "MONTHLY",
        billingPeriod: plan.billingPeriod ?? displayedLabel,
        price: plan.price,
        amount: plan.amount,
        currency: plan.currency,
        planCode: plan.planCode,
      };

      storeMembershipSelection(storedPlan);

      const isPaidPlan =
        typeof plan.amount === "number"
          ? plan.amount > 0
          : Boolean(
              (typeof plan.monthlyPrice === "number" &&
                plan.monthlyPrice > 0) ||
              (typeof plan.annualPrice === "number" && plan.annualPrice > 0)
            );

      if (!isPaidPlan && plan.id) {
        const setupResponse = await setupMembership({
          membershipPlanId: plan.id,
          billingType: storedPlan.billingType,
        });
        const eduKycUrl = resolveEduKycUrlAfterMembershipSetup({
          kycUrl: setupResponse.kycUrl ?? profileResult.data?.kycUrl,
          eduKycUrl: setupResponse.eduKycUrl ?? profileResult.data?.eduKycUrl,
          isKycVerified,
        });

        clearMembershipSelection();

        if (eduKycUrl) {
          window.location.assign(eduKycUrl);
          return;
        }

        navigate(
          resolveRouteAfterSetupMembership({
            isKycVerified,
          }),
          { replace: true }
        );
        return;
      }

      navigate(
        resolveRouteAfterPlanSelection({
          isPaidPlan,
          isKycVerified,
        })
      );
    } catch (error) {
      console.error("[plan] membership selection failed", error);
      toast.error(
        "Unable to check your membership status. Please try again in a moment."
      );
      clearMembershipSelection();
    } finally {
      setPendingPlanId(null);
      void refetchMembershipStatus();
    }
  };

  return (
    <div className="flex flex-col items-center w-full h-screen gap-2">
      <div className="text-2xl font-semibold">Flexible Membership Plans</div>
      <div className="flex items-center gap-4 text-sm">
        Choose a membership plan that works best for you
        <div className="flex flex-col">
          <div className="relative inline-flex bg-tab-item/20 rounded-full w-full">
            <div className="absolute top-1 bottom-1 bg-white rounded-full shadow transition-all duration-300" />
            {tabs.map((tab) => (
              <button
                key={tab.label}
                className={`hover:cursor-pointer relative z-10 flex-1 px-8 py-2 text-sm font-medium rounded-full transition-colors ${
                  activeTab.label === tab.label
                    ? "bg-tab-item text-white"
                    : "text-label-dark"
                }`}
                onClick={() => setActiveLabel(tab.label)}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex py-2 gap-6 flex-wrap justify-center">
        {isLoading ? (
          <div className="flex items-center justify-center w-full py-10">
            <Spinner className="size-6 text-tab-item" />
          </div>
        ) : activePlans.length > 0 ? (
          activePlans.map((plan) => {
            const identifier =
              plan.id ??
              `${plan.planCode ?? ""}-${plan.name}-${activeTab.billingType}`;

            return (
              <TierCard
                key={`${activeTab.label}-${plan.name}`}
                data={plan}
                buttonText={
                  plan.price && plan.billingPeriod
                    ? `Select ${plan.name} Membership`
                    : "Continue"
                }
                loading={pendingPlanId === identifier}
                disabled={
                  pendingPlanId !== null && pendingPlanId !== identifier
                }
                onSelected={() =>
                  handlePlanSelection(
                    plan,
                    activeTab.label,
                    activeTab.billingType
                  )
                }
              />
            );
          })
        ) : (
          <div className="text-sm text-label-dark py-10">
            {isError
              ? "Unable to load membership plans right now. Please try again later."
              : "No membership plans are available at the moment."}
          </div>
        )}
      </div>
    </div>
  );
}
